// Role Management Page with Modal for Add/Edit
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Space, Button, Tag, Modal, notification, Spin } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
	ThemeButton,
	ThemeFilterBtn,
	ThemeForm,
	ThemeInput,
	ThemeMainWrapper,
	ThemeTable,
	ThemeTableCard,
	ThemeModal,
	ThemeSwitch,
} from '../Styles/Theme';
import {
	ForbiddenWrapper,
	ModulesContainer,
	ModuleItem,
	ModuleHeader,
	ModuleName,
	SubModuleItem,
	SubModuleName,
	EmptyModulesMessage,
} from './RoleManagementStyle';
import MobHeader from '../../Components/Header/MobHeader';
import { GreetingText } from '../Dashboard/DashboardStyle';
import {
  getRolesApi,
  getRolesReset,
  deleteRoleApi,
  deleteRoleReset,
  createRoleApi,
  createRoleReset,
  updateRoleApi,
  updateRoleReset,
  getRoleByIdApi,
  getRoleByIdReset,
} from '../../store/actions/roleAction';
import { getModulesApi, getModulesReset } from '../../store/actions/moduleAction';
import { useRolePermissions } from '../../utils/permissions';
import { STORAGE_KEYS, USER_TYPES } from '../../utils/constants';
import { toTitleCase } from '../../utils/helpers';

const RoleManagement = (props) => {
	const {
		getRoles,
		getRolesReset,
		getRolesState,
		deleteRole,
		deleteRoleReset,
		deleteRoleState,
		createRole,
		createRoleReset,
		createRoleState,
		updateRole,
		updateRoleReset,
		updateRoleState,
		getRoleById,
		getRoleByIdReset,
		getRoleByIdState,
		getModules,
		getModulesReset,
		getModulesState,
	} = props;

  const [form] = ThemeForm.useForm();
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 12,
    search: '',
  });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [modalOpen, setModalOpen] = useState([false, '', null]); // [isOpen, title, editId]
  const [formData, setFormData] = useState({
    display_name: '',
    permissions: [],
  });
  const [moduleList, setModuleList] = useState([]);
  const [pendingRolePermissions, setPendingRolePermissions] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);
  // Check permissions using hook
  const { can, permissions } = useRolePermissions();
  const canCreate = can('create');
  const canEdit = can('edit');
  const canDelete = can('delete');

  const isEdit = modalOpen[2] !== null;

	// Fetch roles and modules
	useEffect(() => {
		if (permissions.authorized) {
			getRoles(filters);
			getModules({ type: 'super-admin' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, permissions.authorized]);

	// Handle roles response
	useEffect(() => {
		if (getRolesState.apiState === 'success' && getRolesState.data) {
			// Handle both response structures: { roles: [...] } or { data: { roles: [...] } } or { all_roles: [...] }
			const rolesData = getRolesState.data.data || getRolesState.data;
			const roles = rolesData.roles || rolesData.all_roles || [];
			if (roles && roles.length > 0) {
				setTotalRecords(rolesData.pagination?.total || rolesData.total_count || getRolesState.data.pagination?.total || getRolesState.data.total_count || 0);
				const updatedRoles = roles.map((item) => ({
					key: item._id,
					id: item._id,
					role: item.role,
					display_name: item.display_name,
					type: item.type,
					permissions_count: item.permissions?.length || 0,
					createdAt: item.createdAt,
				}));
				setTableData(updatedRoles);
			} else {
				setTableData([]);
				setTotalRecords(0);
			}
			getRolesReset();
		} else if (getRolesState.apiState === 'error') {
			notification.error({
				message: 'Error',
				description: getRolesState.message || 'Failed to fetch roles',
			});
			getRolesReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getRolesState]);

	// Handle modules response
	useEffect(() => {
		if (getModulesState.apiState === 'success' && getModulesState.data) {
			// Handle both response structures: { modules: [...] } or { data: { modules: [...] } } or { all_modules: [...] }
			const modulesData = getModulesState.data.data || getModulesState.data;
			const modules = modulesData.modules || modulesData.all_modules || [];
			if (modules && modules.length > 0) {
				const formattedModules = modules.map((module) => ({
					id: module._id,
					name: module.module_name,
					sub_modules: (module.sub_modules || []).map((subMod) => ({
						id: subMod._id,
						name: subMod.sub_module_name,
						included: false,
					})),
				}));
				
				// If we have pending role permissions, map them now that modules are loaded
				// Only map if we're in edit mode (modal is open with an editId)
				if (pendingRolePermissions && pendingRolePermissions.length > 0 && modalOpen[0] && modalOpen[2]) {
					const updatedModuleList = formattedModules.map((module) => {
						const modulePermission = pendingRolePermissions.find(
							(perm) => perm.module?._id?.toString() === module.id?.toString() || perm.module?.toString() === module.id?.toString()
						);
						
						if (modulePermission) {
							const updatedSubModules = module.sub_modules.map((subMod) => {
								const isIncluded = modulePermission.sub_modules?.some(
									(subModId) => subModId?._id?.toString() === subMod.id?.toString() || subModId?.toString() === subMod.id?.toString()
								);
								return { ...subMod, included: isIncluded || false };
							});
							return { ...module, sub_modules: updatedSubModules };
						}
						return module;
					});
					setModuleList(updatedModuleList);
					setPendingRolePermissions(null);
				} else {
					setModuleList(formattedModules);
				}
			} else {
				setModuleList([]);
			}
			getModulesReset();
		} else if (getModulesState.apiState === 'error') {
			notification.error({
				message: 'Error',
				description: getModulesState.message || 'Failed to fetch modules',
			});
			getModulesReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getModulesState]);

	// Handle create response
	useEffect(() => {
		if (createRoleState.apiState === 'success') {
			notification.success({
				message: 'Success',
				description: createRoleState.message || 'Role created successfully',
			});
			handleCloseModal();
			getRoles(filters);
		} else if (createRoleState.apiState === 'error') {
			// Handle validation errors
			if (createRoleState.errors) {
				for (const fieldName in createRoleState.errors) {
					if (createRoleState.errors.hasOwnProperty(fieldName)) {
						const errorMessage = createRoleState.errors[fieldName];
						form.setFields([
							{
								name: fieldName,
								errors: [
									errorMessage.replace(
										fieldName,
										toTitleCase(fieldName),
									),
								],
							},
						]);
					}
				}
			}
			notification.error({
				message: 'Error',
				description: createRoleState.message || 'Failed to create role',
			});
		}
		createRoleReset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createRoleState]);

	// Handle update response
	useEffect(() => {
		if (updateRoleState.apiState === 'success') {
			notification.success({
				message: 'Success',
				description: updateRoleState.message || 'Role updated successfully',
			});
			handleCloseModal();
			getRoles(filters);
		} else if (updateRoleState.apiState === 'error') {
			// Handle validation errors
			if (updateRoleState.errors) {
				for (const fieldName in updateRoleState.errors) {
					if (updateRoleState.errors.hasOwnProperty(fieldName)) {
						const errorMessage = updateRoleState.errors[fieldName];
						form.setFields([
							{
								name: fieldName,
								errors: [
									errorMessage.replace(
										fieldName,
										toTitleCase(fieldName),
									),
								],
							},
						]);
					}
				}
			}
			notification.error({
				message: 'Error',
				description: updateRoleState.message || 'Failed to update role',
			});
		}
		updateRoleReset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateRoleState]);

	// Handle delete response
	useEffect(() => {
		if (deleteRoleState.apiState === 'success') {
			notification.success({
				message: 'Success',
				description: deleteRoleState.message || 'Role deleted successfully',
			});
			setDeleteModalVisible(false);
			setSelectedRole(null);
			getRoles(filters);
		} else if (deleteRoleState.apiState === 'error') {
			notification.error({
				message: 'Error',
				description: deleteRoleState.message || 'Failed to delete role',
			});
		}
		deleteRoleReset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deleteRoleState]);

	// Fetch role data when editing
	useEffect(() => {
		if (modalOpen[0] && modalOpen[2]) {
			setIsModalLoading(true);
			getRoleById({ id: modalOpen[2] });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalOpen]);

	// Handle role data response for edit
	useEffect(() => {
		if (getRoleByIdState.apiState === 'loading') {
			setIsModalLoading(true);
		} else if (getRoleByIdState.apiState === 'success' && getRoleByIdState.data && modalOpen[2]) {
			const role = getRoleByIdState.data;
			const roleData = {
				display_name: role.display_name || '',
				permissions: role.permissions || [],
			};
			setFormData(roleData);
			form.setFieldsValue({
				display_name: roleData.display_name,
			});

			// Map existing permissions to moduleList if modules are already loaded
			// Only map if we're in edit mode (modalOpen[2] is set)
			if (role.permissions && role.permissions.length > 0 && modalOpen[2]) {
				if (moduleList.length > 0) {
					// Modules are already loaded, map immediately
					const updatedModuleList = moduleList.map((module) => {
						const modulePermission = role.permissions.find(
							(perm) => perm.module?._id?.toString() === module.id?.toString() || perm.module?.toString() === module.id?.toString()
						);
						
						if (modulePermission) {
							const updatedSubModules = module.sub_modules.map((subMod) => {
								const isIncluded = modulePermission.sub_modules?.some(
									(subModId) => subModId?._id?.toString() === subMod.id?.toString() || subModId?.toString() === subMod.id?.toString()
								);
								return { ...subMod, included: isIncluded || false };
							});
							return { ...module, sub_modules: updatedSubModules };
						}
						return module;
					});
					setModuleList(updatedModuleList);
					setIsModalLoading(false);
				} else {
					// Modules not loaded yet, store permissions to map later
					setPendingRolePermissions(role.permissions);
					setIsModalLoading(false);
				}
			} else {
				setIsModalLoading(false);
			}
		} else if (getRoleByIdState.apiState === 'error') {
			setIsModalLoading(false);
			notification.error({
				message: 'Error',
				description: getRoleByIdState.message || 'Failed to fetch role',
			});
			handleCloseModal();
		}
		if (getRoleByIdState.apiState !== 'loading') {
			getRoleByIdReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getRoleByIdState, modalOpen, moduleList.length]);

	const handleSearch = (value) => {
		setFilters({ ...filters, search: value, page: 1 });
	};

	const handleTableChange = (pagination) => {
		setFilters({
			...filters,
			page: pagination.current,
			limit: pagination.pageSize,
		});
	};

	// Default empty form data
	const defaultFormData = {
		display_name: '',
		permissions: [],
	};

	// Helper to reset moduleList to default (all unchecked)
	const resetModuleListToDefault = () => {
		const resetList = moduleList.map((module) => ({
			...module,
			sub_modules: module.sub_modules.map((subMod) => ({
				...subMod,
				included: false,
			})),
		}));
		setModuleList(resetList);
	};

	const handleAdd = () => {
		// Reset form and state before opening modal
		setFormData({ ...defaultFormData });
		form.resetFields();
		form.setFieldsValue({ ...defaultFormData });
		resetModuleListToDefault();
		setPendingRolePermissions(null);
		setIsModalLoading(false);
		// Refresh modules list
		getModules({ type: 'super-admin' });
		setModalOpen([true, 'Add Role', null]);
	};

	const handleEdit = (record) => {
		// Reset form before fetching edit data to prevent stale data showing
		setFormData({ ...defaultFormData });
		form.resetFields();
		form.setFieldsValue({ ...defaultFormData });
		resetModuleListToDefault();
		setPendingRolePermissions(null);
		setIsModalLoading(true);
		// Only refresh modules if we don't have any loaded yet
		if (moduleList.length === 0) {
			getModules({ type: 'super-admin' });
		}
		setModalOpen([true, 'Edit Role', record.id]);
	};

	const handleCloseModal = () => {
		// Reset everything on close
		form.resetFields();
		setFormData({ ...defaultFormData });
		resetModuleListToDefault();
		setPendingRolePermissions(null);
		setIsModalLoading(false);
		setModalOpen([false, '', null]);
	};

	const handleDelete = (record) => {
		setSelectedRole(record);
		setDeleteModalVisible(true);
	};

	const confirmDelete = () => {
		if (selectedRole) {
			deleteRole({ id: selectedRole.id });
		}
	};

  const subModuleSelected = (checked, moduleKey, subModuleKey) => {
    const updatedModuleList = [...moduleList];
    updatedModuleList[moduleKey] = {
      ...updatedModuleList[moduleKey],
      sub_modules: [...updatedModuleList[moduleKey].sub_modules],
    };
    updatedModuleList[moduleKey].sub_modules[subModuleKey] = {
      ...updatedModuleList[moduleKey].sub_modules[subModuleKey],
      included: checked,
    };
    setModuleList(updatedModuleList);
  };

  const moduleSelected = (value, moduleKey) => {
    const updatedModuleList = moduleList.map((module, index) => {
      if (index === moduleKey) {
        const updatedSubModules = module.sub_modules.map((subModule) => {
          return { ...subModule, included: value };
        });
        return { ...module, sub_modules: updatedSubModules };
      }
      return module;
    });
    setModuleList(updatedModuleList);
  };

  const isAnySubModuleUnchecked = (moduleKey) => {
    const selectedModule = moduleList[moduleKey];
    if (!selectedModule) return false;
    const uncheckedSubModule = selectedModule.sub_modules.find(
      (subModule) => !subModule.included
    );
    return !!uncheckedSubModule;
  };

  const handleSubmit = (values) => {
    // Validate at least one permission is selected
    const allIncludedFalse = moduleList.every((module) =>
      module.sub_modules.every((subModule) => !subModule.included)
    );
    
    if (allIncludedFalse) {
      form.setFields([
        {
          name: 'permissions',
          errors: ['Please give access to at least one module'],
        },
      ]);
      return;
    }

    // Format permissions for API
    const permissions = moduleList
      .filter((module) => module.sub_modules.some((subMod) => subMod.included))
      .map((module) => ({
        module: module.id,
        sub_modules: module.sub_modules
          .filter((subMod) => subMod.included)
          .map((subMod) => subMod.id),
      }));

    const submitData = {
      ...values,
      permissions,
    };

		if (isEdit) {
			updateRole({ id: modalOpen[2], ...submitData });
		} else {
			createRole(submitData);
		}
	};

  const columns = [
    {
      title: 'Role Name',
      dataIndex: 'display_name',
      key: 'display_name',
      sorter: true,
    },
    {
      title: 'Slug',
      dataIndex: 'role',
      key: 'role',
      render: (role) => <Tag color="blue">{role}</Tag>,
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions_count',
      key: 'permissions_count',
      render: (count) => `${count} module(s)`,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          {canEdit && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  if (!permissions.authorized) {
    return (
      <ThemeMainWrapper>
        <MobHeader />
        <ForbiddenWrapper>
          <h2>403 - Forbidden</h2>
          <p>You do not have permission to access this page.</p>
        </ForbiddenWrapper>
      </ThemeMainWrapper>
    );
  }

  return (
    <>
      <MobHeader />
      <div className="customContainer">
        <Row gutter={[16, 16]} className="mb-3" align="middle">
          <Col xs={20} sm={12} md={12} lg={12}>
            <GreetingText>Role Management</GreetingText>
          </Col>
          <Col xs={4} sm={12} md={12} lg={12} className="text-end">
            {canCreate && (
              <>
                {isMobile ? (
                  <ThemeButton
                    size="large"
                    shape="square"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                  />
                ) : (
                  <ThemeButton
                    size="large"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                  >
                    New Role
                  </ThemeButton>
                )}
              </>
            )}
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="mb-1">
          <Col xs={24} sm={12} md={8} lg={8}>
            <ThemeInput
              placeholder="Search roles..."
              size="large"
              prefix={<SearchOutlined />}
              allowClear
              onPressEnter={(e) => handleSearch(e.target.value)}
              onChange={(e) => {
                if (!e.target.value) {
                  handleSearch('');
                }
              }}
            />
          </Col>
          {/* <Col xs={24} sm={12} md={8} lg={8}>
            <ThemeFilterBtn size="large" shape="square">
              Filter
            </ThemeFilterBtn>
          </Col> */}
        </Row>
      </div>
      <div className="customBorders">
        <ThemeTable
          columns={columns}
          dataSource={tableData}
          loading={getRolesState.apiState === 'loading'}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: totalRecords,
            showSizeChanger: true,
            showTotal: total => `Total ${total} roles`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </div>

      {/* Add/Edit Modal */}
      <ThemeModal
        title={modalOpen[1]}
        centered
        open={modalOpen[0]}
        onCancel={handleCloseModal}
        footer={null}
        width={850}
      >
        {isModalLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Loading...</div>
          </div>
        ) : (
          <ThemeForm
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={formData}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <ThemeForm.Item
                  label="Role Name"
                  name="display_name"
                  rules={[{ required: true, message: 'Please enter role name' }]}
                  extra="A unique slug will be auto-generated from this name"
                >
                  <ThemeInput size="large" placeholder="e.g., Survey Manager, Admin, Viewer" />
                </ThemeForm.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]} className="mt-4">
              <Col xs={24}>
                <ThemeForm.Item
                  label="Permissions"
                  name="permissions"
                  rules={[
                    {
                      validator: () => {
                        const allIncludedFalse = moduleList.every((module) =>
                          module.sub_modules.every((subModule) => !subModule.included)
                        );
                        if (allIncludedFalse) {
                          return Promise.reject('Please give access to at least one module');
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <ModulesContainer>
                    {moduleList.length > 0 ? (
                      moduleList.map((item, moduleKey) => (
                        <ModuleItem key={moduleKey}>
                          <ModuleHeader>
                            <ModuleName>{item.name}</ModuleName>
                            <ThemeSwitch
                              size="small"
                              checked={!isAnySubModuleUnchecked(moduleKey)}
                              onChange={(value) => moduleSelected(value, moduleKey)}
                            />
                          </ModuleHeader>
                          <Row gutter={[16, 16]}>
                            {item.sub_modules.map((subMod, subModuleKey) => (
                              <Col xs={12} sm={8} md={6} key={subModuleKey}>
                                <SubModuleItem>
                                  <SubModuleName>{subMod.name}</SubModuleName>
                                  <ThemeSwitch
                                    size="small"
                                    checked={subMod.included || false}
                                    onChange={(checked) =>
                                      subModuleSelected(checked, moduleKey, subModuleKey)
                                    }
                                  />
                                </SubModuleItem>
                              </Col>
                            ))}
                          </Row>
                        </ModuleItem>
                      ))
                    ) : (
                      <EmptyModulesMessage>
                        No modules available. Please create modules first.
                      </EmptyModulesMessage>
                    )}
                  </ModulesContainer>
                </ThemeForm.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]} className="mt-4">
              <Col xs={24} className="text-end">
                <Space>
                  <Button onClick={handleCloseModal}>Cancel</Button>
                  <ThemeButton
                    type="primary"
                    htmlType="submit"
                    loading={createRoleState.apiState === 'loading' || updateRoleState.apiState === 'loading'}
                  >
                    {isEdit ? 'Update Role' : 'Create Role'}
                  </ThemeButton>
                </Space>
              </Col>
            </Row>
          </ThemeForm>
        )}
      </ThemeModal>

      {/* Delete Modal */}
      <Modal
        title="Delete Role"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedRole(null);
        }}
        confirmLoading={deleteRoleState.apiState === 'loading'}
      >
        <p>
          Are you sure you want to delete role "{selectedRole?.display_name}"?
        </p>
        <p>This action cannot be undone.</p>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
	getRolesState: state.role.getRolesState,
	deleteRoleState: state.role.deleteRoleState,
	createRoleState: state.role.createRoleState,
	updateRoleState: state.role.updateRoleState,
	getRoleByIdState: state.role.getRoleByIdState,
	getModulesState: state.module.getModulesState,
});

const mapDispatchToProps = (dispatch) => ({
	getRoles: (params) => dispatch(getRolesApi(params)),
	getRolesReset: () => dispatch(getRolesReset()),
	deleteRole: (params) => dispatch(deleteRoleApi(params.id)),
	deleteRoleReset: () => dispatch(deleteRoleReset()),
	createRole: (params) => dispatch(createRoleApi(params)),
	createRoleReset: () => dispatch(createRoleReset()),
	updateRole: (params) => dispatch(updateRoleApi(params.id, params)),
	updateRoleReset: () => dispatch(updateRoleReset()),
	getRoleById: (params) => dispatch(getRoleByIdApi(params.id)),
	getRoleByIdReset: () => dispatch(getRoleByIdReset()),
	getModules: (params) => dispatch(getModulesApi(params)),
	getModulesReset: () => dispatch(getModulesReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(RoleManagement);
